import { Tooltip } from '@unmind/design-system';
import { rem, transparentize } from 'polished';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { TextBlock } from 'icons';
import { ScreenReaderContent } from '../Accessibility';
import { small } from '../../utils';

const Button = styled.button<{ isAudioPlayer?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  color: ${props =>
    props.isAudioPlayer
      ? transparentize(0.2, props.theme.colors.mediaPlayer.audio.icon.default)
      : transparentize(0.2, props.theme.colors.staticPalette.white)};
  cursor: pointer;
  padding: 0;
  height: ${rem(24)};
  width: ${rem(24)};
  position: relative;

  ${small(css`
    height: ${rem(32)};
    width: ${rem(32)};
  `)}

  &:hover {
    color: ${props =>
      props.isAudioPlayer
        ? props.theme.colors.mediaPlayer.audio.icon.hover
        : props.theme.colors.staticPalette.white};
  }

  &:active,
  &:focus {
    outline: ${props => css`
      ${rem(2)} solid ${props.theme.button.borderFocusColor}
    `};
  }
`;

const TranscriptIcon = styled(TextBlock).attrs({
  primaryColor: 'currentColor',
})`
  height: 100%;
  width: 100%;
`;

interface TranscriptButtonProps {
  className?: string;
  onClick?(): void;
  isAudioPlayer?: boolean;
}

export const TranscriptButton = ({
  className,
  onClick,
  isAudioPlayer,
}: TranscriptButtonProps) => {
  const { t: translate } = useTranslation('media_player');

  return (
    <Tooltip
      triggerElement={
        <Button
          className={className}
          onClick={onClick}
          isAudioPlayer={isAudioPlayer}
        >
          <TranscriptIcon />
          <ScreenReaderContent as="span">
            {translate('controls.transcript_button.accessibility_label')}
          </ScreenReaderContent>
        </Button>
      }
      tooltipContents={translate('controls.transcript_button.tooltip')}
      accessibilityId="transcript-button-tooltip"
    />
  );
};
